import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
  CircularProgress,
  Alert,
  Link,
  Chip,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import { AutoAwesome, CloseSmall, MailOpen, Refresh } from "@tiny/icons";
import { Controller, FormProvider } from "react-hook-form";
import { HtmlPreview, MobilePreview } from "../../components";
import SaveTemplateDialog from "../SaveTemplateDialog";
import { GenerateWithAIDialogProps } from "./GenerateWithAIDialog.types";
import { useGenerateWithAIDialog } from "./GenerateWithAIDialog.hooks";
import { useConfigView } from "../../Config.context";

function GenerateWithAIDialog(props: GenerateWithAIDialogProps) {
  const { open, onClose, onCustomizeInEditor } = props;
  const { config } = useConfigView();
  const { navigateTo } = config || {};

  const {
    methods,
    templateType,
    workflowId,
    workflowOptions,
    isLoadingWorkflows,
    generatedHtml,
    generatedCss,
    rawHtml,
    isGenerating,
    generateError,
    hasGenerated,
    handleGenerate,
    handleRegenerate,
  } = useGenerateWithAIDialog(props);

  const { control } = methods;

  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const handleSaveAsTemplate = () => {
    setIsSaveDialogOpen(true);
  };

  const handleCloseSaveDialog = () => {
    setIsSaveDialogOpen(false);
    onClose();
  };

  const handleSaveDialogSuccess = () => {
    setIsSaveDialogOpen(false);
    onClose();
  };

  const handleCustomizeInEditor = () => {
    if (onCustomizeInEditor && generatedHtml) {
      if (templateType === "automation" && rawHtml) {
        onCustomizeInEditor(rawHtml, generatedCss);
      } else {
        onCustomizeInEditor(generatedHtml, generatedCss);
      }
      onClose();
    }
  };

  const handleAITuningClick = () => {
    if (navigateTo) {
      navigateTo("/main/account-settings/ai-tuning");
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            maxHeight: "800px",
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: "rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(10px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AutoAwesome sx={{ fontSize: 28, color: "primary.main" }} />
              </Box>
              <Box>
                <Typography variant="h3">Generate with AI</Typography>
                <Typography variant="p3" sx={{ mt: 1 }}>
                  Create beautiful email templates instantly
                </Typography>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              {hasGenerated && (
                <Button
                  variant="outlined"
                  onClick={handleRegenerate}
                  disabled={isGenerating}
                  startIcon={<Refresh />}
                  size="medium"
                >
                  Regenerate
                </Button>
              )}
              <IconButton onClick={onClose} size="small">
                <CloseSmall />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <Divider sx={{ py: 2 }} />

        <DialogContent sx={{ p: 0, overflow: "hidden" }}>
          <FormProvider {...methods}>
            <Box display="flex" height="100%">
              <Box
                sx={(theme) => ({
                  width: "400px",
                  borderRight: 1,
                  borderColor: "divider",
                  p: 6,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  backgroundColor: theme.palette.grey[10],
                })}
              >
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Typography
                      variant="label1"
                      component="label"
                      color="grey.90"
                    >
                      Template Style
                    </Typography>
                    <Chip
                      label="Required"
                      size="small"
                      sx={(theme) => ({
                        height: 20,
                        fontSize: "0.7rem",
                        backgroundColor: theme.palette.primary[10],
                        color: theme.palette.primary.main,
                      })}
                    />
                  </Box>
                  <Controller
                    name="templateType"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field}>
                        <FormControlLabel
                          value="automation"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="h4">Automation</Typography>
                              <Typography variant="p3" color="text.secondary">
                                Automation style template
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="campaign"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="h4">Campaign</Typography>
                              <Typography variant="p3" color="text.secondary">
                                One-time broadcast style template
                              </Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    )}
                  />
                </Box>

                <Box>
                  <Controller
                    name="workflowId"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <FormControl fullWidth size="small">
                        <InputLabel
                          placeholder="Select"
                          sx={{
                            background: "white",
                            px: 1,
                            fontSize: 14,
                          }}
                        >
                          {templateType === "automation"
                            ? "Select Automation event"
                            : "Select Campaign"}
                        </InputLabel>
                        <Select
                          {...field}
                          label={
                            templateType === "automation"
                              ? "Automation type"
                              : "Campaign type"
                          }
                          displayEmpty
                          disabled={isLoadingWorkflows}
                        >
                          {workflowOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Box>

                {generateError && (
                  <Alert
                    severity="error"
                    sx={{
                      borderRadius: 2,
                      "& .MuiAlert-icon": {
                        fontSize: 24,
                      },
                    }}
                  >
                    {generateError}
                  </Alert>
                )}

                {!hasGenerated && (
                  <Box sx={{ mt: "auto" }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleGenerate}
                      disabled={!workflowId || isGenerating}
                      startIcon={
                        isGenerating ? (
                          <>
                            <CircularProgress
                              size={20}
                              sx={{ mr: 2, color: "white" }}
                            />
                          </>
                        ) : (
                          <AutoAwesome />
                        )
                      }
                      size="large"
                    >
                      {isGenerating ? "Generating..." : "Generate Template"}
                    </Button>
                  </Box>
                )}
              </Box>

              <Box
                sx={(theme) => ({
                  flex: 1,
                  p: 6,
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: theme.palette.grey[20],
                })}
              >
                {!hasGenerated && !isGenerating ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                  >
                    <Box textAlign="center">
                      <Box
                        sx={(theme) => ({
                          width: 220,
                          height: 320,
                          background: `linear-gradient(135deg, ${theme.palette.primary[10]} 0%, ${theme.palette.primary[20]} 100%)`,
                          borderRadius: 3,
                          mb: 4,
                          mx: "auto",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "2px dashed",
                          borderColor: theme.palette.primary.main,
                          position: "relative",
                          overflow: "hidden",
                        })}
                      >
                        <Box
                          sx={(theme) => ({
                            position: "absolute",
                            top: -50,
                            right: -50,
                            width: 150,
                            height: 150,
                            borderRadius: "50%",
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary[120]} 100%)`,
                            opacity: 0.1,
                          })}
                        />
                        <Box textAlign="center" zIndex={1}>
                          <MailOpen
                            sx={(theme) => ({
                              fontSize: 64,
                              color: theme.palette.primary.main,
                              mb: 2,
                            })}
                          />
                          <Typography
                            variant="h3"
                            sx={(theme) => ({
                              color: theme.palette.primary.main,
                            })}
                          >
                            Email Preview
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="h4" color="text.primary" mb={1}>
                        Ready to create something amazing?
                      </Typography>
                      <Typography
                        variant="p2"
                        color="text.secondary"
                        maxWidth={400}
                        mx="auto"
                      >
                        Select your template type and workflow, then click
                        Generate to see AI create a beautiful email template for
                        you
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <>
                    {/* Preview Content */}
                    {hasGenerated && !isGenerating && (
                      <Tabs
                        value={activeTab}
                        onChange={(_, v) => setActiveTab(v)}
                        sx={{ mb: 3 }}
                      >
                        <Tab label="Desktop" />
                        <Tab label="Mobile" />
                      </Tabs>
                    )}
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                      }}
                    >
                      {isGenerating ? (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          height="100%"
                          flexDirection="column"
                          gap={3}
                        >
                          <CircularProgress
                            size={60}
                            sx={(theme) => ({
                              color: theme.palette.primary.main,
                            })}
                          />
                          <Box textAlign="center">
                            <Typography variant="h4" mb={1}>
                              AI is crafting your template
                            </Typography>
                            <Typography variant="p2" color="text.secondary">
                              This will only take a moment...
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          sx={(theme) => ({
                            flex: 1,
                            display: "flex",
                            justifyContent: "center",
                            overflow: "hidden",
                            backgroundColor: theme.palette.grey[0],
                            borderRadius: 3,
                          })}
                        >
                          {activeTab === 0 ? (
                            <HtmlPreview
                              html={generatedHtml}
                              css={generatedCss}
                              width="100%"
                              height="100%"
                              contentWidth={800}
                              scrollable
                            />
                          ) : (
                            <MobilePreview
                              html={generatedHtml}
                              css={generatedCss}
                              width="360px"
                              height="100%"
                              contentWidth={360}
                              scrollable
                            />
                          )}
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ mt: 4 }}>
                      <Box
                        sx={(theme) => ({
                          p: 3,
                          borderRadius: 2,
                          mb: 4,
                          border: "1px solid",
                          borderColor: theme.palette.primary[40],
                        })}
                      >
                        <Typography variant="p2" fontWeight={600} mb={0.5}>
                          💡 Want better results?
                        </Typography>
                        <Typography variant="p2" color="text.secondary">
                          Fine-tune the AI's output by visiting the{" "}
                          <Link
                            component="button"
                            variant="p2"
                            onClick={handleAITuningClick}
                            sx={(theme) => ({
                              cursor: "pointer",
                              fontWeight: 600,
                              color: theme.palette.primary.main,
                              textDecoration: "none",
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            })}
                          >
                            AI tuning page
                          </Link>
                          .
                        </Typography>
                      </Box>

                      {/* Action Buttons */}
                      <Box display="flex" gap={2} justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          onClick={handleSaveAsTemplate}
                          disabled={isGenerating}
                        >
                          Save as Template
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleCustomizeInEditor}
                          disabled={isGenerating}
                        >
                          Customize in Editor
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </FormProvider>
        </DialogContent>
      </Dialog>
      <SaveTemplateDialog
        open={isSaveDialogOpen}
        onClose={handleCloseSaveDialog}
        template={{
          templateHtml:
            templateType === "automation" && rawHtml ? rawHtml : generatedHtml,
          templateCss: generatedCss,
          editorType: "DRAG_AND_DROP",
          name: "Template",
        }}
      />
    </>
  );
}

export default GenerateWithAIDialog;
